import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Clock, GraduationCap, Wallet, UserCheck, CheckCircle2 } from "lucide-react";
import api from "../lib/api";

export default function WorkshopDetail() {
  const { id } = useParams();
  const [w, setW] = useState(null);
  useEffect(() => {
    api.get(`/workshops/${id}`).then((r) => setW(r.data)).catch(() => setW(false));
  }, [id]);

  if (w === null) return <div className="min-h-screen bg-[#2A2A2A]"/>;
  if (w === false) return (
    <div className="min-h-screen bg-[#2A2A2A] flex items-center justify-center">
      <div className="text-white text-center">
        <h2 className="font-display text-3xl mb-4">Warsztat nie istnieje</h2>
        <Link to="/warsztaty" className="text-[#FFCC00] underline">Wróć do listy</Link>
      </div>
    </div>
  );

  return (
    <div data-testid="workshop-detail" className="bg-[#2A2A2A] min-h-screen">
      <div className="relative h-[55vh] min-h-[400px]">
        <img src={w.image_url} alt={w.title} className="w-full h-full object-cover"/>
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-[#2A2A2A]"/>
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-[1400px] w-full mx-auto px-5 md:px-10 pb-12">
            <Link to="/warsztaty" className="inline-flex items-center gap-2 text-white/70 hover:text-[#FFCC00] mb-6 font-mono-acc text-xs uppercase">
              <ArrowLeft size={14}/> Wszystkie warsztaty
            </Link>
            <div className="kicker mb-3">Warsztat</div>
            <h1 className="font-display text-white text-4xl md:text-6xl uppercase leading-[0.95] max-w-4xl">
              {w.title}
            </h1>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 flex flex-col md:grid md:grid-cols-3 gap-12">
        <div className="md:col-span-2 order-1 md:order-1">
          <div className="kicker mb-4">Opis</div>
          <p className="text-white/80 text-lg leading-relaxed whitespace-pre-line">{w.description}</p>
        </div>

        <aside className="bg-[#1F1F1F] border border-[#3a3a3a] p-8 h-fit space-y-5 order-2 md:order-2" data-testid="workshop-details-panel">
          <div className="kicker mb-2">Szczegóły</div>
          <DetailRow icon={Clock} label="Czas" value={w.duration || "—"}/>
          <DetailRow icon={GraduationCap} label="Poziom" value={w.level || "—"}/>
          <DetailRow icon={Wallet} label="Koszt" value={w.cost || "—"} highlight/>
          <DetailRow icon={UserCheck} label="Wiek" value={w.age || "—"}/>

          <a href="#zapisy" className="mt-6 block w-full text-center bg-[#FFCC00] text-[#2A2A2A] font-display text-sm py-4 btn-brutal">
            ZAPISZ SIĘ
          </a>
        </aside>

        <div className="md:col-span-2 order-3 md:order-3">
          <SignupForm workshop={w}/>
        </div>
      </div>
    </div>
  );
}

function DetailRow({ icon: Icon, label, value, highlight }) {
  return (
    <div className="flex items-start gap-4 border-b border-[#3a3a3a] pb-4 last:border-b-0 last:pb-0">
      <Icon size={20} className="text-[#FFCC00] mt-1 flex-shrink-0"/>
      <div>
        <div className="font-mono-acc text-[10px] uppercase text-white/50">{label}</div>
        <div className={`mt-1 ${highlight ? "font-display text-[#FFCC00] text-xl uppercase" : "text-white text-base"}`}>
          {value}
        </div>
      </div>
    </div>
  );
}

function SignupForm({ workshop }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setStatus("");
    setLoading(true);
    try {
      await api.post("/signups", { 
        workshop_id: workshop.id, 
        workshop_title: workshop.title,
        ...form 
      });
      setStatus("ok");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      setStatus(err.response?.data?.detail ? `Błąd: ${err.response.data.detail}` : "Błąd wysyłki");
    } finally {
      setLoading(false);
    }
  };

  if (status === "ok") {
    return (
      <div id="zapisy" className="mt-16 bg-[#1F1F1F] border-2 border-[#FFCC00] p-10 text-center" data-testid="signup-success">
        <CheckCircle2 size={48} className="text-[#FFCC00] mx-auto mb-4"/>
        <h3 className="font-display text-white text-2xl uppercase mb-2">Zgłoszenie wysłane!</h3>
        <p className="text-white/70">Skontaktujemy się z Tobą w sprawie potwierdzenia terminu i płatności.</p>
        <button onClick={() => setStatus("")} className="mt-6 font-mono-acc text-xs uppercase text-[#FFCC00] hover:underline">
          Wyślij kolejne zgłoszenie →
        </button>
      </div>
    );
  }

  return (
    <div id="zapisy" className="mt-16">
      <div className="kicker mb-4">Zapisy</div>
      <h3 className="font-display text-white text-3xl md:text-4xl uppercase mb-3">Dołącz do warsztatu</h3>
      <p className="text-white/65 mb-2 max-w-2xl">
        Zostaw kontakt — odezwiemy się w ciągu 24h z propozycją terminu, szczegółami płatności i listą rzeczy do przyniesienia.
      </p>
      <p className="text-[#FFCC00] font-mono-acc text-[11px] uppercase mb-8 max-w-2xl">
        W wiadomości prosimy o podanie liczby osób oraz ich wieku (np. 2 osoby, wiek 12 i 40 lat).
      </p>
      <form onSubmit={submit} className="bg-[#1F1F1F] border border-[#3a3a3a] p-6 md:p-8 grid md:grid-cols-2 gap-4" data-testid="signup-form">
        <div>
          <label className="font-mono-acc text-[10px] uppercase text-white/60">Imię i nazwisko *</label>
          <input
            data-testid="signup-name"
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full mt-2 bg-[#2A2A2A] border border-[#3a3a3a] text-white px-4 py-3 focus:border-[#FFCC00] outline-none"
          />
        </div>
        <div>
          <label className="font-mono-acc text-[10px] uppercase text-white/60">Email *</label>
          <input
            data-testid="signup-email"
            type="email"
            required
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="w-full mt-2 bg-[#2A2A2A] border border-[#3a3a3a] text-white px-4 py-3 focus:border-[#FFCC00] outline-none"
          />
        </div>
        <div className="md:col-span-2">
          <label className="font-mono-acc text-[10px] uppercase text-white/60">Telefon</label>
          <input
            data-testid="signup-phone"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className="w-full mt-2 bg-[#2A2A2A] border border-[#3a3a3a] text-white px-4 py-3 focus:border-[#FFCC00] outline-none"
          />
        </div>
        <div className="md:col-span-2">
          <label className="font-mono-acc text-[10px] uppercase text-white/60">Wiadomość (Liczba osób, wiek, pytania) *</label>
          <textarea
            data-testid="signup-message"
            required
            rows={4}
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            className="w-full mt-2 bg-[#2A2A2A] border border-[#3a3a3a] text-white px-4 py-3 focus:border-[#FFCC00] outline-none"
            placeholder="Np. 2 osoby (wiek 12 i 35 lat), preferowana sobota rano."
          />
        </div>
        {status && status !== "ok" && (
          <div className="md:col-span-2 text-red-400 text-sm" data-testid="signup-error">{status}</div>
        )}
        <div className="md:col-span-2 text-xs text-white/40 leading-relaxed">
          Klikając „Wyślij" akceptujesz przetwarzanie danych osobowych w celu kontaktu w sprawie warsztatu (RODO art. 6 ust. 1 lit. b).
        </div>
        <button
          type="submit"
          disabled={loading}
          data-testid="signup-submit"
          className="md:col-span-2 bg-[#FFCC00] text-[#2A2A2A] font-display py-4 btn-brutal disabled:opacity-50"
        >
          {loading ? "WYSYŁAM..." : "WYŚLIJ ZGŁOSZENIE"}
        </button>
      </form>
    </div>
  );
}
